'use client'

import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { useForm, FormProvider, SubmitHandler } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { isValidPhoneNumber } from 'react-phone-number-input'
import * as yup from 'yup'

import PhoneNumberInputController from '@/components/ui/forms/PhoneNumberInputController'
import SelectInputController from '@/components/ui/inputs/SelectInputController'
import TextInputController from '@/components/ui/inputs/TextInputController'

import { useBooking } from '../../model/bookingContext'
import { Companion } from '../../model/bookingTypes'

const RELATIONSHIP_OPTIONS = [
  // Conjoint / Partenaire
  { value: 'SIGOTHR', name: 'Conjoint' },
  { value: 'HUSB', name: 'Époux' },
  { value: 'WIFE', name: 'Épouse' },
  { value: 'DOMPART', name: 'Concubin(e) ou partenaire PACS' },
  // Parents
  { value: 'MTH', name: 'Mère' },
  { value: 'FTH', name: 'Père' },
  { value: 'NMTH', name: 'Mère biologique' },
  { value: 'NFTH', name: 'Père biologique' },
  { value: 'STPMTH', name: 'Belle-mère - épouse du père ou de la mère' },
  { value: 'STPFTH', name: 'Beau-père - époux du père ou de la mère' },
  // Enfants
  { value: 'CHILD', name: 'Enfant' },
  { value: 'DAUC', name: 'Fille' },
  { value: 'SONC', name: 'Fils' },
  { value: 'DAU', name: 'Fille biologique' },
  { value: 'SON', name: 'Fils biologique' },
  // Fratrie
  { value: 'BRO', name: 'Frère' },
  { value: 'SIS', name: 'Soeur' },
  { value: 'HBRO', name: 'Demi-frère' },
  { value: 'HSIS', name: 'Demi-soeur' },
  // Grands-parents
  { value: 'GRMTH', name: 'Grand-mère' },
  { value: 'GRFTH', name: 'Grand-père' },
  { value: 'GGRMTH', name: 'Arrière-grand-mère' },
  { value: 'GGRFTH', name: 'Arrière-grand-père' },
  // Petits-enfants
  { value: 'GRNDDAU', name: 'Petite-fille' },
  { value: 'GRNDSO', name: 'Petit-fils' },
  // Famille élargie
  { value: 'UNCLE', name: 'Oncle' },
  { value: 'AUNT', name: 'Tante' },
  { value: 'NEPHEW', name: 'Neveu' },
  { value: 'NIECE', name: 'Nièce' },
  { value: 'COUSN', name: 'Cousin(e)' },
  { value: 'FAMMEMB', name: 'Autre membre de la famille' },
  // Proches non-familiaux
  { value: 'FRND', name: 'Autre proche' },
  { value: 'NBOR', name: 'Voisin(e)' },
  { value: 'ROOM', name: 'Personne vivant sous le même toit' },
  // Protection juridique
  { value: 'TUTEUR', name: 'Tuteur' },
  { value: 'CURATEUR', name: 'Curateur' },
  { value: 'MSVG', name: 'Mandataire de sauvegarde' },
]

// Built inside the component (see below) rather than at module scope so the lingui `t`
// messages re-evaluate with the active locale. Without explicit messages, yup emitted its
// English defaults ("firstName is a required field", etc.) even when the UI was translated.
function buildCompanionSchema() {
  return yup
    .object({
      hasCompanion: yup.boolean().default(false),
      firstName: yup.string().when('hasCompanion', {
        is: true,
        then: (schema) => schema.required(t`First name is required`),
        otherwise: (schema) => schema.optional(),
      }),
      lastName: yup.string().when('hasCompanion', {
        is: true,
        then: (schema) => schema.required(t`Last name is required`),
        otherwise: (schema) => schema.optional(),
      }),
      relationshipType: yup.string().when('hasCompanion', {
        is: true,
        then: (schema) => schema.required(t`Please select a relationship`),
        otherwise: (schema) => schema.optional(),
      }),
      email: yup.string().when('hasCompanion', {
        is: true,
        then: (schema) => schema.email(t`Please enter a valid email address`),
        otherwise: (schema) => schema.optional(),
      }),
      phone: yup.string().test('isValidPhoneNumber', t`Invalid phone number`, (value, testContext) => {
        const { countryCode, hasCompanion: withCompanion } = testContext.parent
        if (!withCompanion) return true
        if (!value) return true
        return isValidPhoneNumber(value, countryCode)
      }),
      countryCode: yup.string(),
      confidentialityAgreement: yup.boolean().when('hasCompanion', {
        is: true,
        then: (schema) => schema.oneOf([true], t`You must accept the confidentiality commitment`).required(t`You must accept the confidentiality commitment`),
        otherwise: (schema) => schema.optional(),
      }),
    })
    .test('companion-contact', '', function (values) {
      if (!values.hasCompanion) return true
      if (values.email || values.phone) return true
      // Attach to the form root, not the `email` field: this is a cross-field rule, and
      // pinning it to `email` rendered the message under that input (mid-grid), away from
      // the "email or phone" hint below the grid. We surface it once, in that hint's place.
      return this.createError({
        path: 'root',
        message: t`An email or phone number is required`,
      })
    })
}

type CompanionFormData = yup.InferType<ReturnType<typeof buildCompanionSchema>>

export function CompanionForm() {
  const { state, dispatch, goToNextStep } = useBooking()

  const rhForm = useForm<CompanionFormData>({
    defaultValues: {
      hasCompanion: state.companion != null,
      firstName: state.companion?.firstName || '',
      lastName: state.companion?.lastName || '',
      relationshipType: state.companion?.relationshipType || 'SIGOTHR',
      email: state.companion?.email || '',
      phone: state.companion?.phone || '',
      countryCode: 'FR',
      confidentialityAgreement: state.companion?.confidentialityAgreement || false,
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(
      buildCompanionSchema().shape({
        phone: yup.string().test('isValidPhoneNumber', t`Invalid phone number`, (value, testContext) => {
          const { countryCode, hasCompanion: withCompanion } = testContext.parent
          if (!withCompanion) return true
          if (!value) return true
          return isValidPhoneNumber(value, countryCode)
        }),
      })
    ),
  })

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { isValid, errors },
  } = rhForm
  const hasCompanion = watch('hasCompanion')

  const onSubmit: SubmitHandler<CompanionFormData> = (data) => {
    let payload: Companion | null = null
    if (data.hasCompanion && data.firstName && data.lastName && data.relationshipType) {
      const selectedOption = RELATIONSHIP_OPTIONS.find((o) => o.value === data.relationshipType)
      payload = {
        firstName: data.firstName,
        lastName: data.lastName,
        relationshipType: data.relationshipType,
        relationshipTypeDisplay: selectedOption?.name,
        email: data.email || undefined,
        phone: data.phone || undefined,
        confidentialityAgreement: data.confidentialityAgreement === true,
      }
    }

    // Advance via the provider's goToNextStep (which is language-aware) rather than the
    // reducer's goToNextStep flag: the COMPANION step is only present in the step order when
    // language === 'fr', and the reducer computes determineNextStep() WITHOUT a language, so
    // it can't find the COMPANION step and would never advance (booking gets stuck here).
    dispatch({ type: 'SET_COMPANION', payload })
    goToNextStep({ ...state, companion: payload })
  }

  return (
    <FormProvider {...rhForm}>
      <form className="flex flex-col space-y-4" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex items-start space-x-3">
          <div className="flex h-6 items-center">
            <input
              id="hasCompanion"
              type="checkbox"
              checked={hasCompanion}
              onChange={(e) => setValue('hasCompanion', e.target.checked, { shouldValidate: true })}
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
            />
          </div>
          <label htmlFor="hasCompanion" className="text-sm font-medium text-gray-700 cursor-pointer select-none">
            <Trans>I will be accompanied for this teleconsultation</Trans>
          </label>
        </div>

        {hasCompanion && (
          <div className="border-l-4 border-primary bg-gray-50 rounded-r-lg p-4 sm:p-5 space-y-4">
            <h3 className="text-sm font-semibold text-gray-800">
              <Trans>Companion information</Trans>
            </h3>
            <div className="grid grid-rows-auto sm:grid-cols-2 gap-4">
              <div className="h-fit">
                <TextInputController control={control} fullWidth label={t`First name(s)`} name="firstName" required />
              </div>
              <div className="h-fit">
                <SelectInputController control={control} label={t`Relationship with patient`} name="relationshipType" options={RELATIONSHIP_OPTIONS} required />
              </div>
              <div className="h-fit">
                <TextInputController control={control} fullWidth label={t`Last name`} name="lastName" required />
              </div>
              <div className="h-fit">
                <TextInputController control={control} fullWidth label={t`Email`} name="email" />
              </div>
              <div className="h-fit">
                <PhoneNumberInputController control={control} label={t`Phone`} name="phone" onCountryChange={(code) => setValue('countryCode', code)} />
              </div>
            </div>
            {errors.root?.message ? (
              <p className="text-xs text-red-600">{errors.root.message}</p>
            ) : (
              <p className="text-xs text-gray-400">
                <Trans>At least an email or phone number is required.</Trans>
              </p>
            )}

            <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-start space-x-3">
                <div className="flex h-6 items-center">
                  <input
                    id="confidentialityAgreement"
                    type="checkbox"
                    checked={watch('confidentialityAgreement') || false}
                    onChange={(e) => setValue('confidentialityAgreement', e.target.checked, { shouldValidate: true })}
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                </div>
                <label htmlFor="confidentialityAgreement" className="text-sm text-gray-700 cursor-pointer select-none">
                  <span className="font-semibold">
                    <Trans>Confidentiality commitment</Trans>
                  </span>
                  <br />
                  <span className="text-xs text-gray-500">
                    <Trans>The companion commits to keeping all information exchanged during the teleconsultation confidential.</Trans>
                  </span>
                </label>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end sm:absolute sm:top-full sm:right-0 sm:!m-0">
          <button
            className="inline-flex flex-1 items-center justify-center rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-gray-300 sm:rounded-none"
            disabled={hasCompanion && !isValid}
            type="submit"
          >
            <Trans>Next</Trans>
          </button>
        </div>
      </form>
    </FormProvider>
  )
}
